import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getSource } from '../api/sourcesClient'
import { Button } from '../../../shared/ui/Button'
import { ErrorState } from '../../../shared/ui/States'
import { SourceDetailSkeleton } from './SourceSkeleton'
import { Globe } from 'lucide-react'
import { FacebookIcon, XIcon } from '../../../shared/ui/BrandIcons'
import { normalizePlatform } from '../../posts/model/platforms'

const PlatformIcon = ({ platform }: { platform: string }) => {
  const p = normalizePlatform(platform)
  if (p === 'facebook') return <FacebookIcon size={16} color="currentColor" />
  if (p === 'x') return <XIcon size={16} color="currentColor" />
  return <Globe size={16} />
}

interface SourceDetailProps {
  sourceId: string
  onClose: () => void
}

export function SourceDetail({ sourceId, onClose }: SourceDetailProps) {
  const { t } = useTranslation()
  const {
    data: source,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['sources', sourceId],
    queryFn: ({ signal }) => getSource(sourceId, signal),
  })

  if (isLoading) {
    return (
      <div
        style={{
          padding: 24,
          borderRadius: 12,
          border: '1px solid var(--panel-border)',
          background: 'var(--panel)',
        }}
      >
        <SourceDetailSkeleton />
      </div>
    )
  }

  if (error || !source) {
    return (
      <div
        style={{
          padding: 24,
          borderRadius: 12,
          border: '1px solid var(--panel-border)',
          background: 'var(--panel)',
        }}
      >
        <ErrorState error={error} />
        <Button onClick={onClose} variant="neutral" style={{ marginTop: 16 }}>
          {t('sources.close')}
        </Button>
      </div>
    )
  }

  return (
    <div
      style={{
        padding: 24,
        borderRadius: 12,
        border: '1px solid var(--panel-border)',
        background: 'var(--panel)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
          {t('sources.details')}
        </h3>
        <Button variant="neutral" onClick={onClose}>
          {t('sources.close')}
        </Button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '16px 32px' }}>
        <div style={{ color: 'var(--text-secondary)' }}>{t('sources.name')}</div>
        <div style={{ fontWeight: 500 }}>{source.name}</div>

        <div style={{ color: 'var(--text-secondary)' }}>{t('sources.platform')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <PlatformIcon platform={source.platform} />
          <span>{t(`platforms.${normalizePlatform(source.platform)}`)}</span>
        </div>

        <div style={{ color: 'var(--text-secondary)' }}>{t('sources.username')}</div>
        <div>
          {source.username ? (
            <a
              href={source.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-accent-tint"
              style={{
                padding: '2px 8px',
                borderRadius: 4,
                color: 'var(--accent)',
                fontSize: 13,
                fontWeight: 500,
                textDecoration: 'none',
              }}
            >
              @{source.username}
            </a>
          ) : (
            <span style={{ color: 'var(--text-muted)' }}>-</span>
          )}
        </div>

        <div style={{ color: 'var(--text-secondary)' }}>{t('sources.url')}</div>
        <div>
          {source.url ? (
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--accent)' }}
            >
              {source.url}
            </a>
          ) : (
            <span style={{ color: 'var(--text-muted)' }}>-</span>
          )}
        </div>

        <div style={{ color: 'var(--text-secondary)' }}>{t('sources.description')}</div>
        <div>
          {source.description || <span style={{ color: 'var(--text-muted)' }}>-</span>}
        </div>

        <div style={{ color: 'var(--text-secondary)' }}>{t('sources.status')}</div>
        <div>
          <span
            className={
              source.isEnabled
                ? 'bg-status-enabled-surface text-status-enabled'
                : 'bg-status-disabled-surface text-status-disabled'
            }
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '3px 10px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {source.isEnabled ? t('sources.enabled') : t('sources.disabled')}
          </span>
        </div>

        <div style={{ color: 'var(--text-secondary)' }}>{t('sources.columnCreated')}</div>
        <div>{new Date(source.createdAt).toLocaleString()}</div>

        <div style={{ color: 'var(--text-secondary)' }}>{t('sources.columnUpdated')}</div>
        <div>{new Date(source.updatedAt).toLocaleString()}</div>
      </div>
    </div>
  )
}
